/**
 * QuickTags Component
 * 
 * Reusable UI component for displaying and inserting email variable tags.
 * Used across all Portal email modals (EmailCoach, BulkEmail, ThreadReply).
 * 
 * Features:
 * - Displays tags as colored badges
 * - Handles click to insert tag
 * - Supports disabled state
 * - Fully controlled by parent (no internal state for tags)
 */

import React, { useState } from 'react';
import { Link as LinkIcon } from 'react-feather';
import { QuickTag } from '../utils/emailVariables';
import { buildLinkMarkdown } from '../utils/emailLinks';
import { TextInput } from './ui';

interface QuickTagsProps {
  tags: QuickTag[];
  onInsertTag: (tag: QuickTag) => void;
  disabled?: boolean;
  title?: string;
  compact?: boolean;
}

// Link chips ride the same onInsertTag pipe as variable tags — the value is
// the [Label](url) syntax the integration service renders into an anchor.
const LINK_TAG_COLOR = '#1D4ED8';
const LINK_TAG_BG = '#DBEAFE';

/**
 * QuickTags UI Component
 * 
 * @param tags - Array of quick tag objects to display
 * @param onInsertTag - Callback when a tag is clicked
 * @param disabled - Whether tags are disabled (e.g., during send)
 * @param title - Optional title override (default: "Quick Tags")
 */
export const QuickTags: React.FC<QuickTagsProps> = ({
  tags,
  onInsertTag,
  disabled = false,
  title = 'Quick Tags',
  compact = false
}) => {
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const closeLinkForm = () => {
    setShowLinkForm(false);
    setLinkLabel('');
    setLinkUrl('');
    setLinkError(null);
  };

  const handleInsertLink = () => {
    const markdown = buildLinkMarkdown(linkLabel, linkUrl);
    if (!markdown) {
      setLinkError('Enter a valid URL, e.g. https://youtube.com/@yourchannel');
      return;
    }
    onInsertTag({ label: 'Link', value: markdown, color: LINK_TAG_COLOR, bgColor: LINK_TAG_BG });
    closeLinkForm();
  };

  const header = (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: compact ? '8px' : '12px'
    }}>
      <div style={{
        fontSize: compact ? '13px' : '14px',
        fontWeight: '500',
        color: '#000000'
      }}>
        {title}
      </div>
      <button
        onClick={() => (showLinkForm ? closeLinkForm() : setShowLinkForm(true))}
        disabled={disabled}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: compact ? '3px 8px' : '6px 12px',
          backgroundColor: LINK_TAG_BG,
          color: LINK_TAG_COLOR,
          border: 'none',
          borderRadius: compact ? '10px' : '16px',
          fontSize: compact ? '11px' : '12px',
          fontWeight: '500',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1
        }}
      >
        <LinkIcon size={compact ? 11 : 12} />
        Insert link
      </button>
    </div>
  );

  const linkForm = showLinkForm && (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: '8px',
      padding: '10px',
      backgroundColor: '#F8FAFC',
      border: '1px solid #E2E8F0',
      borderRadius: '8px',
      marginBottom: compact ? '8px' : '12px'
    }}>
      {/* The two boxes keep their flex bases and their compact padding: this row
        * sits inside a composer toolbar, not a form column, and the primitive's
        * full-width 12/14 would push the Insert button onto its own line. */}
      <TextInput
        type="text"
        value={linkLabel}
        onChange={(e) => setLinkLabel(e.target.value)}
        placeholder="Link text (e.g. My YouTube)"
        aria-label="Link text"
        style={{
          width: 'auto',
          flex: '1 1 140px',
          padding: '6px 10px',
          fontSize: '13px'
        }}
      />
      <TextInput
        type="text"
        value={linkUrl}
        onChange={(e) => { setLinkUrl(e.target.value); setLinkError(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleInsertLink(); } }}
        placeholder="https://…"
        aria-label="Link URL"
        invalid={!!linkError}
        style={{
          width: 'auto',
          flex: '2 1 180px',
          padding: '6px 10px',
          fontSize: '13px'
        }}
      />
      <button
        onClick={handleInsertLink}
        style={{
          padding: '6px 14px',
          backgroundColor: LINK_TAG_COLOR,
          color: '#FFFFFF',
          border: 'none',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '500',
          cursor: 'pointer'
        }}
      >
        Insert
      </button>
      <button
        onClick={closeLinkForm}
        style={{
          padding: '6px 10px',
          backgroundColor: 'transparent',
          color: '#6B7280',
          border: 'none',
          fontSize: '13px',
          cursor: 'pointer'
        }}
      >
        Cancel
      </button>
      {linkError && (
        <div style={{ flexBasis: '100%', color: '#EF4444', fontSize: '12px' }}>
          {linkError}
        </div>
      )}
    </div>
  );

  // Group tags by category if they have categories
  const hasCategories = tags.some(tag => tag.category);
  
  if (hasCategories) {
    // Group tags by category
    const categories: Record<string, QuickTag[]> = {};
    tags.forEach(tag => {
      const category = tag.category || 'Other';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(tag);
    });

    return (
      <div style={{ marginTop: compact ? '8px' : '16px' }}>
        {header}
        {linkForm}
        {Object.entries(categories).map(([category, categoryTags]) => (
          <div key={category} style={{ marginBottom: compact ? '8px' : '12px' }}>
            <div style={{
              fontSize: compact ? '10px' : '12px',
              fontWeight: '600',
              color: '#6B7280',
              marginBottom: compact ? '4px' : '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {category}
            </div>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: compact ? '4px' : '8px'
            }}>
              {categoryTags.map((tag, index) => (
                <button
                  key={`${category}-${index}`}
                  onClick={() => onInsertTag(tag)}
                  disabled={disabled}
                  style={{
                    padding: compact ? '3px 8px' : '6px 12px',
                    backgroundColor: tag.bgColor,
                    color: tag.color,
                    border: 'none',
                    borderRadius: compact ? '10px' : '16px',
                    fontSize: compact ? '11px' : '12px',
                    fontWeight: '500',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'opacity 0.2s',
                    opacity: disabled ? 0.5 : 1,
                    lineHeight: compact ? '1.3' : undefined
                  }}
                  onMouseOver={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.opacity = '0.8';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!disabled) {
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                >
                  {tag.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Simple flat list if no categories
  return (
    <div style={{ marginTop: '16px' }}>
      {header}
      {linkForm}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        {tags.map((tag, index) => (
          <button
            key={index}
            onClick={() => onInsertTag(tag)}
            disabled={disabled}
            style={{
              padding: '6px 12px',
              backgroundColor: tag.bgColor,
              color: tag.color,
              border: 'none',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: 'opacity 0.2s',
              opacity: disabled ? 0.5 : 1
            }}
            onMouseOver={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = '0.8';
              }
            }}
            onMouseOut={(e) => {
              if (!disabled) {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            {tag.label}
          </button>
        ))}
      </div>
    </div>
  );
};
